import React from 'react';
import { Box, Card, CardContent, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatDurationForDisplay } from '../../utils/durationUtils';
import { getLocalizedValue } from '../../constants/Languages';
import { BRAND } from '../../theme/brandTokens';

const actionButtonSx = {
  width: { xs: 40, sm: 32 },
  height: { xs: 40, sm: 32 },
};

const ServiceComboCard = ({
  combo,
  services,
  currency,
  loading,
  canEdit,
  onEdit,
  onDelete,
}) => {
  const { t, i18n } = useTranslation();
  const isActive = !combo.disabled;
  const hasIncludedConsent = (combo.serviceIds || []).some((serviceId) => {
    const service = (services || []).find((item) => item.id === serviceId);
    return Boolean(service?.consentFormId);
  });
  const serviceNames = (combo.serviceIds || []).map((serviceId) => {
    const service = (services || []).find((item) => item.id === serviceId);
    return service
      ? getLocalizedValue(service.name, i18n.language, undefined, t('serviceCombosManagement.card.unnamed'))
      : t('serviceCombosManagement.card.removedService');
  });

  const renderActionButtons = () => (
    <>
      <Tooltip title={t('serviceCombosManagement.actions.edit')}>
        <IconButton
          size="small"
          aria-label={t('serviceCombosManagement.actions.edit')}
          onClick={onEdit}
          disabled={loading}
          sx={{ ...actionButtonSx, color: '#1976d2' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('serviceCombosManagement.actions.delete')}>
        <IconButton
          size="small"
          aria-label={t('serviceCombosManagement.actions.delete')}
          onClick={onDelete}
          disabled={loading}
          sx={{ ...actionButtonSx, color: '#d32f2f' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <Card
      id={`service-combo-card-${combo.id}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        width: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, wordBreak: 'break-word', lineHeight: 1.3 }}>
            {getLocalizedValue(combo.name, i18n.language, undefined, t('serviceCombosManagement.card.unnamed'))}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {serviceNames.join(' + ')}
          </Typography>
        </Box>

        <Box sx={{ mt: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: canEdit ? 0.75 : 0 }}>
          <Chip
            size="small"
            label={isActive ? t('servicesManagement.status.active') : t('servicesManagement.status.inactive')}
            sx={{
              backgroundColor: isActive ? '#e8f5e9' : '#ffebee',
              color: isActive ? '#2e7d32' : '#c62828',
            }}
          />
          {hasIncludedConsent ? (
            <Chip
              size="small"
              label={t('serviceCombosManagement.card.consentFromServices')}
              sx={{ backgroundColor: BRAND.surface, color: BRAND.text, border: `1px solid ${BRAND.line}` }}
            />
          ) : null}
        </Box>

        {canEdit ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {renderActionButtons()}
          </Box>
        ) : null}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, pt: 1.5, mt: 1.5, borderTop: `1px solid ${BRAND.line}` }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('servicesManagement.card.duration')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatDurationForDisplay(combo.duration)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('servicesManagement.card.price')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {Number(combo.price) === 0
                ? t('servicesManagement.card.free')
                : formatCurrency(combo.price, currency)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ServiceComboCard;
